import React from 'react'
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
import HelpOutline from '@mui/icons-material/HelpOutline'
import WarningAmber from '@mui/icons-material/WarningAmber'
import BoxInc from '../../shared-ui/BoxInc/Index'
import {
  CHECK_STATUS,
  checkTitle,
  conditionText,
  documentLabel,
  documentState,
  hasAnythingToShow,
  normaliseChecklist,
  normaliseChecks,
  sortChecks,
  summariseChecks,
  summaryText
} from './claimChecksLib.es6'

const CHECK_VIEW = {
  flagged: {
    label: 'Flagged for review',
    Icon: WarningAmber,
    color: '#7a4a00',
    background: '#fff8e1',
    border: '2px solid #b26a00'
  },
  not_evaluated: {
    label: 'Not checked',
    Icon: HelpOutline,
    color: '#4a4f55',
    background: '#fafafa',
    border: '1px dashed #8a9097'
  },
  passed: {
    label: 'Passed',
    Icon: CheckCircleOutline,
    color: '#1e6b2e',
    background: '#ffffff',
    border: '1px solid #5f9f6b'
  }
}

const DOCUMENT_VIEW = {
  provided: { label: 'Provided', Icon: CheckCircleOutline, color: '#1e6b2e' },
  not_matched: {
    label: 'Not matched yet',
    Icon: HelpOutline,
    color: '#7a4a00'
  },
  unknown: { label: 'Not checked', Icon: HelpOutline, color: '#4a4f55' }
}

const listStyle = { listStyle: 'none', margin: 0, padding: 0 }

const rowStyle = view => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: '8px',
  padding: '8px 12px',
  borderRadius: '4px',
  color: view.color,
  background: view.background,
  border: view.border
})

const noteStyle = { fontSize: '13px', color: '#4a4f55', margin: '8px 0' }

function Checks({ checks }) {
  const sorted = sortChecks(checks)
  return (
    <ul style={listStyle} aria-label="Claim checks">
      {sorted.map((check, index) => {
        const view = CHECK_VIEW[check.status]
        const Icon = view.Icon
        return (
          <li key={check.code + index} style={rowStyle(view)}>
            <Icon aria-hidden="true" style={{ marginRight: '10px' }} />
            <div>
              <div
                style={{
                  fontWeight:
                    check.status === CHECK_STATUS.FLAGGED ? 'bold' : 'normal'
                }}
              >
                <span>{view.label}</span>
                {': '}
                <span>{checkTitle(check.code)}</span>
              </div>
              {check.message ? <div>{check.message}</div> : null}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function Documents({ items }) {
  return (
    <ul style={listStyle} aria-label="Required documents">
      {items.map((item, index) => {
        const view = DOCUMENT_VIEW[documentState(item)]
        const Icon = view.Icon
        return (
          <li
            key={item.code + index}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '6px',
              color: view.color
            }}
          >
            <Icon aria-hidden="true" style={{ marginRight: '10px' }} />
            <div>
              <span style={{ fontWeight: 'bold' }}>
                {documentLabel(item.code)}
              </span>
              {' - '}
              <span>{view.label}</span>
              <span style={{ color: '#4a4f55' }}>
                {item.mandatory ? ' (mandatory)' : ' (optional)'}
                {item.conditional ? ` (${conditionText(item)})` : ''}
                {item.alternatives.length
                  ? ` (or ${item.alternatives.map(documentLabel).join(', ')})`
                  : ''}
              </span>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

export default function ClaimChecks({ claim }) {
  if (!hasAnythingToShow(claim)) return null

  const items = normaliseChecklist(claim.required_documents_checklist)
  const checks = normaliseChecks(claim.claim_checks)
  const summary = summariseChecks(checks)
  const enforced = claim.required_documents_enforced === true

  return (
    <BoxInc
      title="Product claim checks"
      style={{ marginTop: '30px' }}
      expanded={summary.flagged > 0 || enforced}
    >
      <div role="region" aria-label="Product claim checks">
        {checks.length > 0 ? (
          <div>
            <p
              style={{ fontWeight: 'bold', margin: '0 0 8px' }}
              aria-live="polite"
            >
              {summaryText(summary)}
            </p>
            <Checks checks={checks} />
            <p style={noteStyle}>
              Flagged checks are for an assessor to decide and never decline a
              claim. "Not checked" means the check could not be run.
            </p>
          </div>
        ) : null}
        {items.length > 0 ? (
          <div>
            <p style={{ fontWeight: 'bold', margin: '16px 0 8px' }}>
              Documents the product asks for (from its claim configuration)
            </p>
            <Documents items={items} />
            <p style={noteStyle}>
              {enforced
                ? 'This product requires every mandatory document before the claim can be marked documents complete.'
                : 'Advisory only: this list does not stop the claim moving on.'}
              {
                ' This list is separate from the claim documents box above, which has its own required list. Documents are matched to it by what the uploader called them, so a document uploaded under another name can show as not matched yet.'
              }
            </p>
          </div>
        ) : null}
      </div>
    </BoxInc>
  )
}
